import React, { useState, useEffect, useRef } from "react";
import { db, auth } from "../firebase";
import { 
  collection, doc, getDoc, updateDoc, setDoc, getDocs, query, where, arrayUnion, arrayRemove, serverTimestamp, onSnapshot 
} from "firebase/firestore";

// ==========================================
// CLIENT-SIDE ENCRYPTION UTILITY LAYER
// ==========================================
const chatEncryption = {
  encrypt: (text) => {
    try {
      return btoa(encodeURIComponent(text));
    } catch (e) {
      return text;
    }
  },
  decrypt: (cipherText) => {
    try {
      return decodeURIComponent(atob(cipherText));
    } catch (e) {
      return cipherText;
    }
  }
};

// ==========================================
// REAL-TIME FIRESTORE DATABASE SERVICE LAYER
// ==========================================
const firebaseService = {
  subscribeToUserData: (currentUserId, callback) => {
    const userRef = doc(db, "users", currentUserId);
    
    return onSnapshot(userRef, async (userSnap) => {
      if (!userSnap.exists()) return;
      const userData = userSnap.data();

      let followsList = [];
      let blockedDetailsList = [];
      const rawFollows = [...(userData.followers || []), ...(userData.following || [])];
      const blockedUsers = userData.blockedUsers || [];
      const uniqueFollowsUIDs = [...new Set([...rawFollows, ...blockedUsers])];

      if (uniqueFollowsUIDs.length > 0) {
        const chunks = [];
        for (let i = 0; i < uniqueFollowsUIDs.length; i += 30) {
          chunks.push(uniqueFollowsUIDs.slice(i, i + 30));
        }

        for (const chunk of chunks) {
          const followsQuery = query(collection(db, "users"), where("__name__", "in", chunk));
          const followsSnap = await getDocs(followsQuery);
          const chunkDocs = followsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          followsList = [...followsList, ...chunkDocs];
        }
      }

      const chatsQuery = query(collection(db, "chats"), where("participants", "array-contains", currentUserId));
      
      const unsubscribeChats = onSnapshot(chatsQuery, (chatsSnap) => {
        const chatsList = chatsSnap.docs.map(d => {
          const data = d.data();
          const decryptedMessages = (data.messages || []).map(msg => ({
            ...msg,
            text: chatEncryption.decrypt(msg.text)
          }));
          return { id: d.id, ...data, messages: decryptedMessages };
        });

        callback({
          profile: userData,
          follows: followsList.filter(u => !blockedUsers.includes(u.id)),
          blockedUsersDetails: followsList.filter(u => blockedUsers.includes(u.id)),
          chats: chatsList
        });
      });

      return unsubscribeChats;
    });
  },
  
  searchUserByUsername: async (usernameQuery) => {
    const usersQuery = query(
      collection(db, "users"), 
      where("name", ">=", usernameQuery),
      where("name", "<=", usernameQuery + "\uf8ff")
    );
    const snap = await getDocs(usersQuery);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  },

  sendMessageToDatabase: async (chatId, currentUserId, recipientId, plainMessageText) => {
    const chatRef = doc(db, "chats", chatId);
    const chatSnap = await getDoc(chatRef);
    const encryptedText = chatEncryption.encrypt(plainMessageText);

    const newMessage = {
      sender: currentUserId,
      text: encryptedText,
      timestamp: Date.now(),
      status: "sent" 
    };

    if (!chatSnap.exists()) {
      await setDoc(chatRef, {
        participants: [currentUserId, recipientId],
        messages: [newMessage],
        deletedFor: [],
        createdAt: serverTimestamp()
      });
    } else {
      await updateDoc(chatRef, {
        messages: arrayUnion(newMessage),
        deletedFor: [] 
      });
    }
  },

  markMessagesAsRead: async (chatId, currentUserId) => {
    const chatRef = doc(db, "chats", chatId);
    const chatSnap = await getDoc(chatRef);
    if (!chatSnap.exists()) return;

    const chatData = chatSnap.data();
    let hasChanges = false;
    
    const updatedMessages = (chatData.messages || []).map(msg => {
      if (msg.sender !== currentUserId && msg.status !== "seen") {
        hasChanges = true;
        return { ...msg, status: "seen" };
      }
      return msg;
    });

    if (hasChanges) {
      await updateDoc(chatRef, { messages: updatedMessages });
    }
  },

  hideChatForUser: async (chatId, userId) => {
    const chatRef = doc(db, "chats", chatId);
    await updateDoc(chatRef, {
      deletedFor: arrayUnion(userId)
    });
  },

  toggleBlockUser: async (currentUserId, targetUserId, isBlocked) => {
    const userRef = doc(db, "users", currentUserId);
    await updateDoc(userRef, {
      blockedUsers: isBlocked ? arrayRemove(targetUserId) : arrayUnion(targetUserId)
    });
  },

  updateUserPresence: async (userId, status) => {
    if (!userId) return;
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        presenceStatus: status,
        lastSeen: serverTimestamp()
      });
    } catch (e) {
      console.error("Error updating presence:", e);
    }
  }
};

export default function FloatingChatWidget({ isOpen, onClose, currentUserId }) {
  const activeUserId = currentUserId || auth.currentUser?.uid;

  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState("chats"); 
  const [databaseData, setDatabaseData] = useState({ profile: null, follows: [], blockedUsersDetails: [], chats: [] });
  const [activeContactId, setActiveContactId] = useState(null); 
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [chatInputValue, setChatInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [newChatAlert, setNewChatAlert] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showBlockMenu, setShowBlockMenu] = useState(false);
  const [toast, setToast] = useState(null);
  
  const [deleteConfirmations, setDeleteConfirmations] = useState({});
  const messagesEndRef = useRef(null);

  const quickEmojis = ["😊", "😂", "❤️", "👍", "🔥", "🚀", "✨", "🎉", "🙏", "😎", "💡", "👋"];

  const showToast = (message, type = "error") => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3500);
  };

  const scrollToBottom = (smooth = false) => {
    requestAnimationFrame(() => {
      if (messagesEndRef.current) {
        const scrollContainer = messagesEndRef.current.parentElement;
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
      }
    });
  };

  useEffect(() => {
    if (activeContactId && !isMinimized) {
      scrollToBottom(false);
      const t1 = setTimeout(() => scrollToBottom(false), 30);
      const t2 = setTimeout(() => scrollToBottom(false), 100);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [activeContactId, isMinimized]);

  useEffect(() => {
    if (activeContactId && !isMinimized) {
      scrollToBottom(true);
    }
  }, [databaseData.chats]);

  useEffect(() => {
    if (!activeUserId) return;

    firebaseService.updateUserPresence(activeUserId, "online");

    const handleVisibilityChange = () => {
      if (document.hidden) {
        firebaseService.updateUserPresence(activeUserId, "offline");
      } else {
        firebaseService.updateUserPresence(activeUserId, "online");
      }
    };

    const handleBeforeUnload = () => {
      firebaseService.updateUserPresence(activeUserId, "offline");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    const intervalId = setInterval(() => {
      if (!document.hidden) {
        firebaseService.updateUserPresence(activeUserId, "online");
      }
    }, 60000);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      firebaseService.updateUserPresence(activeUserId, "offline");
    };
  }, [activeUserId]);

  useEffect(() => {
    if (!activeUserId) return;
    
    let unsubscribeUser = firebaseService.subscribeToUserData(activeUserId, (res) => {
      if (res) setDatabaseData(res);
    });

    return () => {
      if (typeof unsubscribeUser === "function") {
        unsubscribeUser();
      }
    };
  }, [activeUserId]);

  useEffect(() => {
    if (activeTab === "search" && searchQuery.trim().length > 0) {
      firebaseService.searchUserByUsername(searchQuery.trim())
        .then(res => setSearchResults(res.filter(u => u.id !== activeUserId)))
        .catch(err => console.error("Search error:", err));
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, activeTab, activeUserId]);

  useEffect(() => {
    if (activeContactId && activeUserId) {
      firebaseService.markMessagesAsRead(activeContactId, activeUserId);
    }
  }, [activeContactId, databaseData.chats, activeUserId]);

  if (!isOpen) return null;

  const activeContact = databaseData.chats.find(c => c.id === activeContactId);
  
  let activeChatPartner = null;
  if (activeContact) {
    const partnerId = activeContact.participants?.find(id => id !== activeUserId);
    activeChatPartner = databaseData.follows.find(f => f.id === partnerId) || 
      databaseData.blockedUsersDetails.find(b => b.id === partnerId) || {
      id: partnerId,
      name: "Chat User",
      photoURL: "",
      presenceStatus: "offline",
      lastSeen: null
    };
  }

  const blockedUsersList = databaseData.profile?.blockedUsers || [];
  const isPartnerBlocked = activeChatPartner ? blockedUsersList.includes(activeChatPartner.id) : false;
  const didPartnerBlockMe = activeChatPartner?.blockedUsers?.includes(activeUserId) || false;

  const formatLastSeen = (timestamp) => {
    if (!timestamp) return "Offline";
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Last seen today at ${timeString}`;
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return `Last seen yesterday at ${timeString}`;
    return `Last seen ${date.toLocaleDateString()} at ${timeString}`;
  };

  const getPartnerStatusText = (partner) => {
    if (!partner) return "Offline";
    if (partner.presenceStatus === "online") return "Active now";
    return formatLastSeen(partner.lastSeen);
  };

  const formatMessageDateGroup = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    if (date.toDateString() === now.toDateString()) return "Today";
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatInputValue.trim() || !activeContactId || !activeChatPartner) return;
    if (isPartnerBlocked || didPartnerBlockMe) {
      showToast("Access denied.", "error");
      return;
    }

    const isFollowed = databaseData.follows.some(f => f.id === activeChatPartner.id);
    if (!isFollowed) {
      showToast("You can only chat with users in your follows list.", "error");
      return;
    }

    const textToSend = chatInputValue.trim();
    const recipientId = activeChatPartner.id;
    
    setNewChatAlert(true);
    setTimeout(() => setNewChatAlert(false), 4000);

    setDatabaseData(prev => ({
      ...prev,
      chats: prev.chats.map(contact => {
        if (contact.id === activeContactId) {
          return {
            ...contact,
            deletedFor: [],
            messages: [...(contact.messages || []), { sender: activeUserId, text: textToSend, timestamp: Date.now(), status: "sent" }]
          };
        }
        return contact;
      })
    }));
    
    setChatInputValue("");
    setShowEmojiPicker(false);
    scrollToBottom(true);
    
    try {
      await firebaseService.sendMessageToDatabase(activeContactId, activeUserId, recipientId, textToSend);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleOpenChat = (user) => {
    if (blockedUsersList.includes(user.id)) {
      showToast("You have blocked this user. Unblock them to chat.", "error");
      return;
    }

    const isFollowed = databaseData.follows.some(f => f.id === user.id);
    if (!isFollowed) {
      showToast("Messaging restricted: You can only message users who are in your follows list.", "error");
      return;
    }

    const chatId = [activeUserId, user.id].sort().join("_");
    let existingChat = databaseData.chats.find(c => c.id === chatId);
    
    if (!existingChat) {
      const newChatThread = {
        id: chatId,
        participants: [activeUserId, user.id],
        messages: [],
        deletedFor: []
      };
      setDatabaseData(prev => ({ ...prev, chats: [...prev.chats, newChatThread] }));
    }
    setActiveContactId(chatId);
    setActiveTab("chats");
    setShowBlockMenu(false);
  };

  const handleDeleteChatClick = async (e, chatId) => {
    e.stopPropagation();
    const isConfirmed = deleteConfirmations[chatId];

    if (!isConfirmed) {
      setDeleteConfirmations(prev => ({ ...prev, [chatId]: true }));
      setTimeout(() => {
        setDeleteConfirmations(prev => ({ ...prev, [chatId]: false }));
      }, 4000);
    } else {
      setDatabaseData(prev => ({
        ...prev,
        chats: prev.chats.map(c => c.id === chatId ? { ...c, deletedFor: [...(c.deletedFor || []), activeUserId] } : c)
      }));
      setDeleteConfirmations(prev => ({ ...prev, [chatId]: false }));
      await firebaseService.hideChatForUser(chatId, activeUserId);
    }
  };

  const handleToggleBlock = async (targetId = null) => {
    const idToToggle = targetId || activeChatPartner?.id;
    if (!idToToggle) return;
    const isBlockedNow = blockedUsersList.includes(idToToggle);
    try {
      await firebaseService.toggleBlockUser(activeUserId, idToToggle, isBlockedNow);
      setShowBlockMenu(false);
      showToast(isBlockedNow ? "User unblocked successfully." : "User blocked successfully.", "success");
    } catch (err) {
      console.error("Error toggling block status:", err);
      showToast("Failed to update block status.", "error");
    }
  };

  const getFilteredList = () => {
    if (activeTab === "chats") {
      const visibleChats = databaseData.chats.filter(item => {
        const deletedForArray = item.deletedFor || [];
        if (deletedForArray.includes(activeUserId)) return false;

        const partnerId = item.participants?.find(id => id !== activeUserId);
        if (blockedUsersList.includes(partnerId)) return false;

        return true;
      });

      const sortedChats = [...visibleChats].sort((a, b) => {
        const lastMsgA = a.messages?.[a.messages.length - 1]?.timestamp || 0;
        const lastMsgB = b.messages?.[b.messages.length - 1]?.timestamp || 0;
        return lastMsgB - lastMsgA;
      });

      return sortedChats.filter(item => {
        const partnerId = item.participants?.find(id => id !== activeUserId);
        const partner = databaseData.follows.find(f => f.id === partnerId);
        if (!partner) return false; 
        const targetName = partner.name || partner.email || "Chat";
        return targetName.toLowerCase().includes(searchQuery.toLowerCase());
      });
    } else if (activeTab === "follows") {
      return databaseData.follows.filter(item => {
        if (blockedUsersList.includes(item.id)) return false;
        const targetName = item.name || item.email || "";
        return targetName.toLowerCase().includes(searchQuery.toLowerCase());
      });
    } else if (activeTab === "blocked") {
      return databaseData.blockedUsersDetails.filter(item => {
        const targetName = item.name || item.email || "";
        return targetName.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }
    return [];
  };

  const currentList = getFilteredList();

  const renderMessageStatus = (status) => {
    if (status === "seen") {
      return <span style={{ color: "#34a853", fontSize: "11px", fontWeight: "700" }} title="Seen">✓✓</span>;
    } else if (status === "delivered") {
      return <span style={{ color: "#8c939d", fontSize: "11px" }} title="Delivered">✓✓</span>;
    }
    return <span style={{ color: "#8c939d", fontSize: "11px" }} title="Sent">✓</span>;
  };

  if (isMinimized) {
    return (
      <div 
        onClick={() => setIsMinimized(false)}
        style={{
          position: "fixed", bottom: "24px", right: "24px", width: "56px", height: "56px",
          backgroundColor: "#202124", borderRadius: "50%", boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, cursor: "pointer",
          color: "#fff", transition: "transform 0.2s ease"
        }}
        title="Open Chat"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
      </div>
    );
  }

  return (
    <>
      <style>{`
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #f97316; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: #ea580c; }

        @media (max-width: 480px) {
          .floating-chat-container {
            width: 100% !important;
            height: 100% !important;
            bottom: 0 !important;
            right: 0 !important;
            border-radius: 0 !important;
            border: none !important;
          }
        }
      `}</style>
      <div 
        className="floating-chat-container"
        style={{
          position: "fixed", bottom: "24px", right: "24px", width: "380px", height: "560px",
          backgroundColor: "#ffffff", borderRadius: "20px", boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
          display: "flex", flexDirection: "column", zIndex: 1000, overflow: "hidden", border: "1px solid #eaeaea",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
        }}
      >
      
      {/* Modern Toast Notification */}
      {toast && (
        <div style={{
          position: "absolute", top: "16px", left: "50%", transform: "translateX(-50%)",
          backgroundColor: toast.type === "error" ? "#d93025" : "#34a853", color: "#fff",
          padding: "8px 16px", borderRadius: "12px", fontSize: "12px", fontWeight: "600",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)", zIndex: 1100, display: "flex", alignItems: "center", gap: "6px"
        }}>
          {toast.message}
        </div>
      )}

      {newChatAlert && (
        <div style={{ backgroundColor: "#f1f3f4", color: "#3c4043", padding: "8px 12px", fontSize: "11px", textAlign: "center", fontWeight: "600", borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", flexShrink: 0 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
          End-to-End Encrypted Session
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", fontSize: "13px", color: "#666" }}>
          Loading...
        </div>
      ) : !activeContactId ? (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#fff" }}>
          
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {databaseData.profile?.photoURL ? (
                <img src={databaseData.profile.photoURL} alt="Profile" style={{ width: "38px", height: "38px", borderRadius: "50%", objectFit: "cover" }} />
              ) : (
                <div style={{ width: "38px", height: "38px", borderRadius: "50%", backgroundColor: "#202124", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "600", fontSize: "14px" }}>
                  {databaseData.profile?.name?.[0] || "U"}
                </div>
              )}
              <div>
                <div style={{ fontSize: "15px", fontWeight: "700", color: "#111" }}>
                  {databaseData.profile?.name || "My Account"}
                </div>
                <div style={{ fontSize: "11px", color: "#70757a", display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: "#34a853", display: "inline-block" }}></span>
                  Active now
                </div>
              </div>
            </div>
            
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <button 
                onClick={() => { setActiveTab(activeTab === "search" ? "chats" : "search"); setSearchQuery(""); }}
                title="Find user"
                style={{ background: activeTab === "search" ? "#202124" : "#f8f9fa", color: activeTab === "search" ? "#fff" : "#3c4043", border: "1px solid #dadce0", borderRadius: "50%", width: "34px", height: "34px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}
              >
                {activeTab === "search" ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                )}
              </button>
              <button onClick={() => setIsMinimized(true)} title="Minimize" style={{ background: "none", border: "none", cursor: "pointer", color: "#70757a", padding: "6px" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              </button>
              <button onClick={onClose} title="Close" style={{ background: "none", border: "none", cursor: "pointer", color: "#70757a", padding: "6px" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
          </div>

          {activeTab !== "search" && (
            <div style={{ display: "flex", borderBottom: "1px solid #eaeaea", backgroundColor: "#fafafa", flexShrink: 0 }}>
              {[
                { id: "chats", label: `Chats` },
                { id: "follows", label: `Follows` },
                { id: "blocked", label: `Blocked (${blockedUsersList.length})` }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setSearchQuery(""); }}
                  style={{
                    flex: 1, padding: "10px 0", background: "none", border: "none",
                    borderBottom: activeTab === tab.id ? "2px solid #202124" : "2px solid transparent",
                    fontSize: "12px", fontWeight: activeTab === tab.id ? "600" : "500",
                    color: activeTab === tab.id ? "#202124" : "#70757a", cursor: "pointer",
                    transition: "all 0.2s"
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <div style={{ padding: "10px 16px", borderBottom: "1px solid #f0f0f0", backgroundColor: "#fff", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", backgroundColor: "#f1f3f4", borderRadius: "12px", padding: "0 12px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#70757a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: "8px" }}><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              <input 
                type="text" 
                placeholder={activeTab === "search" ? "Search follows..." : `Search ${activeTab}...`} 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: "100%", border: "none", outline: "none", backgroundColor: "transparent", padding: "8px 0", fontSize: "13px", color: "#202124" }}
              />
            </div>
          </div>

          <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
            {activeTab === "search" ? (
              searchResults.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#80868b", fontSize: "13px" }}>
                  {searchQuery.trim() ? "No matching follows found." : "Type a name to search your follows."}
                </div>
              ) : (
                searchResults
                  .filter(user => databaseData.follows.some(f => f.id === user.id) && !blockedUsersList.includes(user.id))
                  .map(user => (
                    <div key={user.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: "1px solid #f8f9fa" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                        {user.photoURL ? (
                          <img src={user.photoURL} alt={user.name} style={{ width: "38px", height: "38px", borderRadius: "50%", objectFit: "cover" }} />
                        ) : (
                          <div style={{ width: "38px", height: "38px", borderRadius: "50%", backgroundColor: "#e0e0e0", color: "#3c4043", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "600", fontSize: "13px" }}>
                            {(user.name || "U")[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div style={{ fontSize: "14px", fontWeight: "600", color: "#202124" }}>{user.name || "User"}</div>
                          <div style={{ fontSize: "11px", color: "#70757a" }}>{getPartnerStatusText(user)}</div>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleOpenChat(user)}
                        style={{ backgroundColor: "#202124", color: "#fff", border: "none", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}
                      >
                        Chat
                      </button>
                    </div>
                  ))
              )
            ) : currentList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#80868b", fontSize: "13px" }}>
                No {activeTab} available.
              </div>
            ) : (
              currentList.map(item => {
                let displayName = "Unknown";
                let photoURL = "";
                let subtext = "";
                let clickTargetUser = null;
                let unreadCount = 0;

                if (activeTab === "chats") {
                  const partnerId = item.participants?.find(id => id !== activeUserId);
                  const partner = databaseData.follows.find(f => f.id === partnerId);
                  if (!partner) return null;
                  
                  displayName = partner.name || partner.email || "Chat";
                  photoURL = partner.photoURL || "";
                  clickTargetUser = partner;
                  
                  const lastMessage = item.messages?.[item.messages.length - 1];
                  subtext = lastMessage ? lastMessage.text : "No messages yet";

                  unreadCount = (item.messages || []).filter(
                    msg => msg.sender !== activeUserId && msg.status !== "seen"
                  ).length;
                } else if (activeTab === "blocked") {
                  displayName = item.name || item.email || "Blocked User";
                  photoURL = item.photoURL || "";
                  subtext = "Blocked";
                } else {
                  displayName = item.name || item.email || "User";
                  photoURL = item.photoURL || "";
                  subtext = getPartnerStatusText(item);
                  clickTargetUser = item;
                }
                
                const isDeleteConfirm = activeTab === "chats" && deleteConfirmations[item.id];

                return (
                  <div 
                    key={item.id || Math.random()}
                    onClick={() => activeTab !== "blocked" && clickTargetUser && handleOpenChat(clickTargetUser)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", cursor: activeTab === "blocked" ? "default" : "pointer", borderBottom: "1px solid #f8f9fa", transition: "background 0.15s" }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
                      {photoURL ? (
                        <img src={photoURL} alt={displayName} style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }} />
                      ) : (
                        <div style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#e0e0e0", color: "#3c4043", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "600", fontSize: "14px" }}>
                          {displayName[0].toUpperCase()}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ fontSize: "14px", fontWeight: "600", color: "#202124" }}>{displayName}</div>
                          {activeTab === "chats" && unreadCount > 0 && (
                            <span style={{ backgroundColor: "#202124", color: "#fff", fontSize: "11px", fontWeight: "700", borderRadius: "10px", padding: "2px 6px", minWidth: "18px", textAlign: "center" }}>
                              {unreadCount}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "12px", color: unreadCount > 0 ? "#111" : "#70757a", fontWeight: unreadCount > 0 ? "600" : "normal", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {subtext}
                        </div>
                      </div>
                    </div>

                    {activeTab === "chats" ? (
                      <button 
                        onClick={(e) => handleDeleteChatClick(e, item.id)}
                        title={isDeleteConfirm ? "Click again to confirm" : "Delete chat"}
                        style={{ 
                          backgroundColor: isDeleteConfirm ? "#d93025" : "transparent", 
                          color: isDeleteConfirm ? "#fff" : "#70757a", 
                          border: isDeleteConfirm ? "none" : "1px solid #dadce0", 
                          borderRadius: "8px", padding: "6px 8px", fontSize: "12px", cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s ease"
                        }}
                      >
                        {isDeleteConfirm ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                        )}
                      </button>
                    ) : activeTab === "blocked" ? (
                      <button 
                        onClick={() => handleToggleBlock(item.id)}
                        style={{ backgroundColor: "#1967d2", color: "#fff", border: "none", borderRadius: "8px", padding: "6px 12px", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}
                      >
                        Unblock
                      </button>
                    ) : (
                      <button style={{ backgroundColor: "#202124", color: "#fff", border: "none", borderRadius: "8px", padding: "6px 12px", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
                        Message
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : (

        /* Active Chat Room View */
        <div style={{ display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#fff", position: "relative", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid #f0f0f0", backgroundColor: "#fff", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <button onClick={() => { setActiveContactId(null); setShowEmojiPicker(false); setShowBlockMenu(false); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#202124", display: "flex", alignItems: "center", padding: "0" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
              </button>
              {activeChatPartner?.photoURL ? (
                <img src={activeChatPartner.photoURL} alt={activeChatPartner.name} style={{ width: "34px", height: "34px", borderRadius: "50%", objectFit: "cover" }} />
              ) : (
                <div style={{ width: "34px", height: "34px", borderRadius: "50%", backgroundColor: "#e0e0e0", color: "#3c4043", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "600" }}>
                  {activeChatPartner?.name?.[0] || "U"}
                </div>
              )}
              <div>
                <div style={{ fontSize: "14px", fontWeight: "600", color: "#202124" }}>{activeChatPartner?.name || "Chat"}</div>
                <div style={{ fontSize: "10px", color: "#70757a", display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ 
                    width: "6px", height: "6px", borderRadius: "50%", 
                    backgroundColor: activeChatPartner?.presenceStatus === "online" ? "#34a853" : "#9aa0a6", 
                    display: "inline-block" 
                  }}></span>
                  {getPartnerStatusText(activeChatPartner)}
                </div>
              </div>
            </div>
            
            <div style={{ display: "flex", alignItems: "center", gap: "4px", position: "relative" }}>
              <button 
                onClick={() => setShowBlockMenu(!showBlockMenu)} 
                title="Chat Options" 
                style={{ background: "none", border: "none", cursor: "pointer", color: "#70757a", padding: "6px", fontSize: "16px", fontWeight: "bold" }}
              >
                ⋮
              </button>
              <button onClick={() => setIsMinimized(true)} title="Minimize" style={{ background: "none", border: "none", cursor: "pointer", color: "#70757a", padding: "4px" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              </button>
              <button onClick={onClose} title="Close" style={{ background: "none", border: "none", cursor: "pointer", color: "#70757a", padding: "4px" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>

              {showBlockMenu && (
                <div style={{
                  position: "absolute", top: "38px", right: "8px", backgroundColor: "#fff",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.15)", borderRadius: "10px", border: "1px solid #e0e0e0",
                  zIndex: 20, minWidth: "140px", overflow: "hidden"
                }}>
                  <button 
                    onClick={() => handleToggleBlock()}
                    style={{
                      width: "100%", padding: "10px 14px", background: "none", border: "none",
                      textAlign: "left", fontSize: "13px", fontWeight: "600", cursor: "pointer",
                      color: isPartnerBlocked ? "#1967d2" : "#d93025"
                    }}
                  >
                    {isPartnerBlocked ? "Unblock User" : "Block User"}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={{ flex: 1, padding: "16px", overflowY: "auto", minHeight: 0, display: "flex", flexDirection: "column", gap: "8px", backgroundColor: "#f8f9fa" }}>
            {activeContact?.messages && activeContact.messages.length > 0 ? (
              (() => {
                let lastDateGroup = null;
                return activeContact.messages.map((msg, index) => {
                  const isMe = msg.sender === activeUserId;
                  const currentDateGroup = formatMessageDateGroup(msg.timestamp);
                  const showDateDivider = currentDateGroup !== lastDateGroup;
                  lastDateGroup = currentDateGroup;

                  const messageTime = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "";

                  return (
                    <React.Fragment key={index}>
                      {showDateDivider && (
                        <div style={{ display: "flex", justifyContent: "center", margin: "12px 0 8px 0" }}>
                          <span style={{ backgroundColor: "#e8eaed", color: "#5f6368", fontSize: "11px", fontWeight: "600", padding: "4px 10px", borderRadius: "10px" }}>
                            {currentDateGroup}
                          </span>
                        </div>
                      )}
                      <div style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start", marginBottom: "4px" }}>
                        <div style={{ 
                          maxWidth: "75%", padding: "10px 14px", borderRadius: "16px", fontSize: "13.5px", 
                          backgroundColor: isMe ? "#ffedd5" : "#ffffff", color: "#202124", 
                          boxShadow: "0 1px 2px rgba(0,0,0,0.04)", wordBreak: "break-word", border: "1px solid #ffd8b5" 
                        }}>
                          <div style={{ whiteSpace: "pre-wrap" }}>{msg.text}</div>
                          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "4px", marginTop: "3px" }}>
                            <span style={{ fontSize: "10px", color: "#70757a" }}>{messageTime}</span>
                            {isMe && renderMessageStatus(msg.status)}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                });
              })()
            ) : (
              <div style={{ textAlign: "center", color: "#80868b", fontSize: "12px", marginTop: "40px" }}>
                Start a conversation! Messages are synced in real-time.
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {showEmojiPicker && !isPartnerBlocked && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "6px", padding: "10px", backgroundColor: "#f1f3f4", borderTop: "1px solid #e0e0e0", flexShrink: 0 }}>
              {quickEmojis.map((emoji, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setChatInputValue(prev => prev + emoji)}
                  style={{ background: "#fff", border: "1px solid #dadce0", borderRadius: "8px", fontSize: "18px", padding: "6px", cursor: "pointer", textAlign: "center" }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          {isPartnerBlocked ? (
            <div style={{ padding: "16px", textAlign: "center", backgroundColor: "#f1f3f4", color: "#5f6368", fontSize: "13px", borderTop: "1px solid #e0e0e0", fontWeight: "500", flexShrink: 0 }}>
              You have blocked this user. <button onClick={() => handleToggleBlock()} style={{ background: "none", border: "none", color: "#1967d2", fontWeight: "600", cursor: "pointer", padding: 0, textDecoration: "underline" }}>Unblock</button> to continue chatting.
            </div>
          ) : (
            <form onSubmit={handleSendMessage} style={{ display: "flex", alignItems: "flex-end", padding: "12px 14px", borderTop: "1px solid #eaeaea", backgroundColor: "#fff", gap: "8px", flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                title="Add Emoji"
                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", padding: "2px 0" }}
              >
                😊
              </button>
              <textarea 
                placeholder="Type a message..." 
                value={chatInputValue}
                onChange={(e) => setChatInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
                rows={3}
                style={{ 
                  flex: 1, border: "1px solid #dadce0", borderRadius: "12px", padding: "10px 14px", 
                  fontSize: "14px", lineHeight: "1.4", outline: "none", backgroundColor: "#fff", 
                  color: "#202124", resize: "none", minHeight: "45px", maxHeight: "100px", 
                  overflowY: "auto", fontFamily: "inherit"
                }}
              />
              <button 
                type="submit" 
                disabled={!chatInputValue.trim()}
                style={{ 
                  background: "none", border: "none", color: chatInputValue.trim() ? "#f97316" : "#cbd5e1", 
                  fontWeight: "700", fontSize: "13.5px", cursor: chatInputValue.trim() ? "pointer" : "default", 
                  paddingBottom: "12px" 
                }}
              >
                Send
              </button>
            </form>
          )}
        </div>
      )}
    </div>
    </>
  );
}